import * as dagre from '@dagrejs/dagre';

import {
  convertMinutesToUnit,
  TIME_UNIT_CONVERSION,
  TimeUnitEnum,
} from '../../../campaigns/marketing-campaign-builder/helpers';
import { NodeType } from '../slides/workflow-editor/models';

import type { WorkflowBuilderState } from './builder-context';
import type { WorkflowCampaignEdge, WorkflowCampaignNode } from '../slides/workflow-editor/models';
import type { UtmParameter } from 'src/app/queries/marketing/get-campaigns';
import type {
  WorkflowEdge,
  WorkflowMarketingCampaign,
  WorkflowNode,
} from 'src/app/queries/marketing/get-workflow-campaign';
import type { ValidationError } from 'yup';

export const getSaveWorkflowCampaignPayload = (
  newFields: Partial<WorkflowMarketingCampaign>,
  existingState: WorkflowBuilderState
) => {
  const utmParameters: UtmParameter[] = [];
  if (existingState.utmTerm) {
    utmParameters.push({ ParameterName: 'utm_term', ParameterValue: existingState.utmTerm });
  }

  const payload: WorkflowMarketingCampaign = {
    MarketingCampaignId: existingState.marketingCampaignId,
    CampaignName: newFields.CampaignName ?? existingState.campaignName,
    CampaignStatusId: newFields.CampaignStatusId ?? existingState.campaignStatusId,
    DelayUnitId: newFields.DelayUnitId ?? existingState.delayUnitId,
    DelayInMinutes: newFields.DelayInMinutes ?? existingState.delayInMinutes,
    Edges: newFields.Edges ?? getWorkflowEdgesFromReactFlowEdges(existingState.edges),
    Nodes: newFields.Nodes ?? getWorkflowNodesFromReactFlowNodes(existingState.nodes),
    RecipientSegmentIds: newFields.RecipientSegmentIds ?? existingState.recipientSegmentIds,
    TimeOfDayToSend: newFields.TimeOfDayToSend ?? existingState.timeOfDayToSend,
    EventTypeId: newFields.EventTypeId ?? existingState.eventTypeId,
    LastModifiedDateUTC: newFields.LastModifiedDateUTC ?? existingState.lastModifiedDateUTC,
    LastRuntimeDateUtc: newFields.LastRuntimeDateUtc ?? existingState.lastRuntimeDateUtc,
    LspId: newFields.LspId ?? existingState.lspId,
    ScheduleSendForUtc: newFields.ScheduleSendForUtc ?? existingState.scheduleSendForUtc,
    SendToAllCustomers: newFields.SendToAllCustomers ?? existingState.sendToAllCustomers,
    CustomUtmParameters: newFields.CustomUtmParameters ?? utmParameters,
    TagIds: newFields.TagIds ?? existingState.tagIds,
  };

  return payload;
};

export function getReactFlowEdgesFromWorkflowEdges(
  edges: WorkflowEdge[],
  nodes: WorkflowNode[]
): WorkflowCampaignEdge[] {
  const results = edges.map((edge, idx) => ({
    id: idx.toString(),
    source: edge.Source.toString(),
    target: edge.Target.toString(),
    edgeType: edge.EdgeType,
  }));

  if (nodes.length > 0) {
    results.unshift({
      id: '-1',
      source: '-1', // trigger node
      target: nodes[0].Id.toString(), // the API always returns the root node as the first node
      edgeType: 'next',
    });
  }

  return results;
}

export function getWorkflowEdgesFromReactFlowEdges(edges: WorkflowCampaignEdge[]): WorkflowEdge[] {
  const results = edges.map((edge) => ({
    Source: Number(edge.source),
    Target: Number(edge.target),
    EdgeType: edge.edgeType,
  }));

  return results.filter((x) => x.Source !== -1);
}

export function getPayloadNodesWithUpdatedNode(
  state: WorkflowBuilderState,
  updatedNode: WorkflowCampaignNode
): WorkflowNode[] {
  const results = state.nodes.map((node) => {
    if (node.id === updatedNode.id) {
      return updatedNode;
    }
    return node;
  });

  return getWorkflowNodesFromReactFlowNodes(results);
}

export function getReactFlowNodesFromWorkflowNodes(nodes: WorkflowNode[]): WorkflowCampaignNode[] {
  const results: WorkflowCampaignNode[] = nodes.map((node) => getReactFlowNodeFromWorkflowNode(node));

  const triggerNode: WorkflowCampaignNode = {
    id: '-1',
    type: 'trigger',
    position: { x: 0, y: 0 },
    data: {
      config: {},
      nodeType: NodeType.TRIGGER,
    },
  };

  results.unshift(triggerNode);

  return results;
}

export function getReactFlowNodeFromWorkflowNode(node: WorkflowNode): WorkflowCampaignNode {
  const id = node.Id.toString();
  const position = { x: 0, y: 0 };

  if (node.Type === 'action') {
    if (node.ActionType === 'sendEmail') {
      return {
        id,
        type: 'action',
        position,
        data: {
          actionType: node.ActionType,
          nodeType: NodeType.ACTION,
          config: {
            emailConfig: {
              isActive: node.EmailConfig.IsActive,
              marketingEmailId: node.EmailConfig.MarketingEmailId,
              metaSenderEmail: node.EmailConfig.MetaSenderEmail,
              previewText: node.EmailConfig.PreviewTextLine,
              senderEmail: node.EmailConfig.SenderEmail,
              subjectLine: node.EmailConfig.SubjectLine,
              marketingTemplatePresetId: node.EmailConfig.MarketingTemplatePresetId,
              priority: null,
              utmTerm:
                node.EmailConfig.CustomUtmParameters?.find((utm) => utm.ParameterName === 'utm_term')?.ParameterValue ??
                null,
            },
            pushConfig: null,
            savedTemplateId: node.EmailConfig.MarketingEmailTemplateId,
            smsConfig: null,
            pointBoostConfig: null,
          },
        },
      };
    }

    if (node.ActionType === 'sendPushNotification') {
      return {
        id,
        type: 'action',
        position,
        data: {
          nodeType: NodeType.ACTION,
          actionType: node.ActionType,
          config: {
            emailConfig: null,
            pushConfig: {
              body: node.PushConfig.Body,
              isActive: node.PushConfig.IsActive,
              marketingPushId: node.PushConfig.MarketingPushId,
              redirectPath: node.PushConfig.RedirectPath,
              title: node.PushConfig.Title,
              priority: null,
            },
            savedTemplateId: null,
            smsConfig: null,
            pointBoostConfig: null,
          },
        },
      };
    }

    if (node.ActionType === 'grantPoints') {
      return {
        id,
        type: 'action',
        position,
        data: {
          nodeType: NodeType.ACTION,
          actionType: node.ActionType,
          config: {
            emailConfig: null,
            pushConfig: null,
            savedTemplateId: null,
            smsConfig: null,
            pointBoostConfig: {
              points: node.PointBoostConfig.Points,
            },
          },
        },
      };
    }

    if (node.ActionType === 'sendSMS') {
      return {
        id,
        type: 'action',
        position,
        data: {
          actionType: node.ActionType,
          nodeType: NodeType.ACTION,
          config: {
            emailConfig: null,
            pushConfig: null,
            pointBoostConfig: null,
            savedTemplateId: node.SMSConfig.MarketingEmailTemplateId,
            smsConfig: {
              isActive: node.SMSConfig.IsActive,
              isMMS: node.SMSConfig.IsMMS,
              marketingSMSId: node.SMSConfig.MarketingSMSId,
              marketingTemplatePresetId: node.SMSConfig.MarketingTemplatePresetId,
              leafbuyerImageTemplateId: node.SMSConfig.LeafbuyerImageTemplateId,
              leafbuyerMessageTemplateId: node.SMSConfig.LeafbuyerMessageTemplateId,
              leafbuyerSMSMarketingConfigId: node.SMSConfig.LeafbuyerSMSMarketingConfigId,
              priority: null,
              utmTerm:
                node.SMSConfig.CustomUtmParameters?.find((utm) => utm.ParameterName === 'utm_term')?.ParameterValue ??
                null,
            },
          },
        },
      };
    }
  }

  if (node.Type === 'conditional') {
    if (
      node.ConditionalType === 'clickedEmail' ||
      node.ConditionalType === 'openedEmail' ||
      node.ConditionalType === 'openedSMS' ||
      node.ConditionalType === 'openedPush'
    ) {
      return {
        id,
        type: 'conditional',
        position,
        data: {
          nodeType: NodeType.CONDITIONAL,
          conditionalType: node.ConditionalType,
          config: {
            conditionalReferenceNodeId: node.ConditionalReferenceNodeId,
            durationInMinutes: durationToMinutes(node.Duration),
            segmentIds: [],
            timeUnitId: node.TimeUnitId,
          },
        },
      };
    }
    if (node.ConditionalType === 'madeAPurchase') {
      return {
        id,
        type: 'conditional',
        position,
        data: {
          nodeType: NodeType.CONDITIONAL,
          conditionalType: node.ConditionalType,
          config: {
            conditionalReferenceNodeId: null,
            durationInMinutes: durationToMinutes(node.Duration),
            segmentIds: null,
            timeUnitId: node.TimeUnitId,
          },
        },
      };
    }
    if (node.ConditionalType === 'isInSegment') {
      return {
        id,
        type: 'conditional',
        position,
        data: {
          nodeType: NodeType.CONDITIONAL,
          conditionalType: node.ConditionalType,
          config: {
            segmentIds: node.SegmentIds,
            conditionalReferenceNodeId: null,
            durationInMinutes: null,
            timeUnitId: null,
          },
        },
      };
    }
  }

  if (node.Type === 'timeDelay') {
    return {
      id,
      type: 'timeDelay',
      position,
      data: {
        nodeType: NodeType.TIME_DELAY,
        config: {
          durationInMinutes: durationToMinutes(node.Duration),
          timeOfDayToSend: node.TimeOfDay,
          timeUnitId: node.TimeUnitId,
        },
      },
    };
  }

  const _neverHappens: never = node;
  throw new Error('Function not implemented.');
}

export function getWorkflowNodesFromReactFlowNodes(nodes: WorkflowCampaignNode[]): WorkflowNode[] {
  const results = nodes.map((node) => getWorkflowNodeFromReactFlowNode(node));
  return results.filter((x) => x !== null);
}

export function getWorkflowNodeFromReactFlowNode(node: WorkflowCampaignNode): WorkflowNode | null {
  const id = Number(node.id);
  const { data } = node;

  if (data.nodeType === NodeType.TRIGGER) {
    return null;
  }

  if (data.nodeType === NodeType.ACTION) {
    if (data.actionType === 'sendEmail') {
      return {
        Id: id,
        Type: 'action',
        ActionType: data.actionType,
        EmailConfig: {
          IsActive: data.config.emailConfig?.isActive ?? true,
          MarketingEmailId: data.config.emailConfig?.marketingEmailId ?? null,
          MarketingEmailTemplateId: data.config.savedTemplateId,
          MarketingTemplatePresetId: data.config.emailConfig?.marketingTemplatePresetId ?? null,
          MetaSenderEmail: data.config.emailConfig?.metaSenderEmail ?? null,
          PreviewTextLine: data.config.emailConfig?.previewText ?? '',
          ReplyToEmail: null,
          SenderEmail: data.config.emailConfig?.senderEmail ?? null,
          SubjectLine: data.config.emailConfig?.subjectLine ?? '',
          Priority: data.config.emailConfig?.priority ?? null,
          CustomUtmParameters: data.config.emailConfig?.utmTerm
            ? [{ ParameterName: 'utm_term', ParameterValue: data.config.emailConfig.utmTerm }]
            : [],
        },
      };
    }

    if (data.actionType === 'sendPushNotification') {
      return {
        Id: id,
        Type: 'action',
        ActionType: data.actionType,
        PushConfig: {
          Body: data.config.pushConfig?.body ?? '',
          IsActive: data.config.pushConfig?.isActive ?? true,
          MarketingPushId: data.config.pushConfig?.marketingPushId ?? null,
          RedirectPath: data.config.pushConfig?.redirectPath ?? '',
          Title: data.config.pushConfig?.title ?? '',
          Priority: data.config.pushConfig?.priority ?? null,
        },
      };
    }

    if (data.actionType === 'sendSMS') {
      return {
        Id: id,
        Type: 'action',
        ActionType: data.actionType,
        SMSConfig: {
          IsActive: data.config.smsConfig?.isActive ?? true,
          IsMMS: data.config.smsConfig?.isMMS ?? false,
          MarketingEmailTemplateId: data.config.savedTemplateId,
          MarketingTemplatePresetId: data.config.smsConfig?.marketingTemplatePresetId ?? null,
          MarketingSMSId: data.config.smsConfig?.marketingSMSId ?? null,
          LeafbuyerImageTemplateId: data.config.smsConfig?.leafbuyerImageTemplateId ?? null,
          LeafbuyerMessageTemplateId: data.config.smsConfig?.leafbuyerMessageTemplateId ?? null,
          LeafbuyerSMSMarketingConfigId: data.config.smsConfig?.leafbuyerSMSMarketingConfigId ?? null,
          Priority: data.config.smsConfig?.priority ?? null,
          CustomUtmParameters: data.config.smsConfig?.utmTerm
            ? [{ ParameterName: 'utm_term', ParameterValue: data.config.smsConfig.utmTerm }]
            : [],
        },
      };
    }

    if (data.actionType === 'grantPoints') {
      return {
        Id: id,
        Type: 'action',
        ActionType: data.actionType,
        PointBoostConfig: {
          Points: data.config.pointBoostConfig?.points ?? 0,
        },
      };
    }

    const _neverHappens: never = data.actionType;
    throw new Error('Function not implemented.');
  }

  if (data.nodeType === NodeType.CONDITIONAL) {
    if (
      data.conditionalType === 'clickedEmail' ||
      data.conditionalType === 'openedEmail' ||
      data.conditionalType === 'openedSMS' ||
      data.conditionalType === 'openedPush'
    ) {
      return {
        Id: id,
        Type: 'conditional',
        ConditionalType: data.conditionalType,
        ConditionalReferenceNodeId: null, // we don't handle this correctly yet, so don't pass it
        Duration: minutesToDuration(data.config.durationInMinutes),
        TimeUnitId: data.config.timeUnitId || 0,
      };
    }

    if (data.conditionalType === 'madeAPurchase') {
      return {
        Id: id,
        Type: 'conditional',
        ConditionalType: data.conditionalType,
        Duration: minutesToDuration(data.config.durationInMinutes),
        TimeUnitId: data.config.timeUnitId || 0,
      };
    }

    if (data.conditionalType === 'isInSegment') {
      return {
        Id: id,
        Type: 'conditional',
        ConditionalType: data.conditionalType,
        SegmentIds: data.config.segmentIds || [],
      };
    }

    const _neverHappens: never = data.conditionalType;
    throw new Error('Function not implemented.');
  }

  if (data.nodeType === NodeType.TIME_DELAY) {
    return {
      Id: id,
      Type: 'timeDelay',
      Duration: minutesToDuration(data.config.durationInMinutes),
      TimeOfDay: data.config.timeOfDayToSend,
      TimeUnitId: data.config.timeUnitId || 0,
    };
  }

  const _neverHappens: never = data;
  throw new Error('Function not implemented.');
}

export const computePositions = (
  nodes: WorkflowCampaignNode[],
  edges: WorkflowCampaignEdge[],
  isReadOnly = false
): WorkflowCampaignNode[] => {
  if (nodes.length === 1) {
    return nodes;
  }
  const graph = new dagre.graphlib.Graph();
  graph.setGraph({
    rankdir: 'TB',
    align: 'DL',
    nodesep: 80,
    ranksep: isReadOnly ? 150 : 100,
  });

  const yesRank = new Map<string, number>();

  let toVisit = new Set<string>();
  toVisit.add('-1');

  while (toVisit.size > 0) {
    const next = new Set<string>();

    for (const edge of edges) {
      if (toVisit.has(edge.source)) {
        next.add(edge.target);
      }

      if (edge.edgeType === 'yes') {
        yesRank.set(edge.target, (yesRank.get(edge.source) ?? 0) + 1);
      } else {
        yesRank.set(edge.target, yesRank.get(edge.source) ?? 0);
      }
    }

    toVisit = next;
  }

  const sortedNodes = nodes.sort((a, b) => {
    const aRank = yesRank.get(a.id) ?? 0;
    const bRank = yesRank.get(b.id) ?? 0;
    return aRank - bRank;
  });

  const sortedEdges = edges.sort((a, b) => {
    const aRank = yesRank.get(a.target) ?? 0;
    const bRank = yesRank.get(b.target) ?? 0;
    return aRank - bRank;
  });

  graph.setDefaultEdgeLabel(() => ({}));

  const yesMap = new Map<string, string>();
  const noMap = new Map<string, string>();

  sortedEdges.forEach((edge) => {
    if (edge.edgeType === 'yes') {
      yesMap.set(edge.source, edge.target);
    } else if (edge.edgeType === 'no') {
      noMap.set(edge.source, edge.target);
    }
  });

  sortedNodes.forEach((node) => {
    graph.setNode(node.id, {
      width: 300,
      height: 100,
    });

    if (node.type === 'conditional' && yesMap.has(node.id) && !noMap.has(node.id)) {
      const dummyId = `${node.id}-dummy`;
      graph.setNode(dummyId, {
        width: 300,
        height: 100,
      });

      graph.setEdge(node.id, dummyId);
    }
  });

  sortedEdges.forEach((edge) => {
    graph.setEdge(edge.source, edge.target);
  });

  dagre.layout(graph, { disableOptimalOrderHeuristic: true });

  return nodes.map((node) => {
    const { x, y } = graph.node(node.id);
    return { ...node, position: { x, y } };
  });
};

export function durationToMinutes(duration: string): number {
  if (!duration) {
    return 0;
  }

  let days = 0;
  let timeSpecifier = duration;
  // Parse duration string in the format "d.HH:MM:SS"
  const dayParts = duration.split('.');
  if (dayParts.length === 2) {
    [days, timeSpecifier] = [parseInt(dayParts[0], 10), dayParts[1]];
  }

  const parts = timeSpecifier.split(':');
  if (parts.length !== 3) {
    return 0;
  }

  const hours = parseInt(parts[0], 10);
  const minutes = parseInt(parts[1], 10);

  // Convert all components to minutes using the TIME_UNIT_CONVERSION constants
  // Days: 1440 minutes per day
  // Hours: 60 minutes per hour
  // Minutes: 1 minute per minute
  const totalMinutes = days * TIME_UNIT_CONVERSION.days + hours * TIME_UNIT_CONVERSION.hours + minutes;

  return totalMinutes;
}

export function minutesToDuration(minutes: number | null): string {
  if (!minutes) {
    return '00:00:00';
  }

  const days = Math.floor(minutes / 1440);
  const remainingMinutesAfterDays = minutes % 1440;
  const hours = Math.floor(remainingMinutesAfterDays / 60);
  const remainingMinutes = remainingMinutesAfterDays % 60;

  // Format parts with leading zeros
  const formattedDays = String(days).padStart(2, '0');
  const formattedHours = String(hours).padStart(2, '0');
  const formattedMinutes = String(remainingMinutes).padStart(2, '0');

  return `${formattedDays}.${formattedHours}:${formattedMinutes}:00`;
}

/**
 * Converts a time string from 24-hour format to 12-hour format with AM/PM
 * @param timeString - Time string in the format "HH:MM:SS"
 * @returns Time string in 12-hour format "h:MM AM/PM"
 */
export function formatTimeString(timeString: string): string {
  if (!timeString || timeString.trim() === '') {
    return '';
  }

  // Extract hours and minutes from the timeString
  const parts = timeString.split(':');
  if (parts.length < 2) {
    return timeString; // Return original if format is invalid
  }

  const hours = parseInt(parts[0], 10);
  const minutes = parseInt(parts[1], 10);

  // Validate input
  if (Number.isNaN(hours) || Number.isNaN(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
    return timeString; // Return original if values are invalid
  }

  // Convert to 12-hour format
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12; // Convert 0 to 12 for 12 AM
  const formattedMinutes = String(minutes).padStart(2, '0');

  return `${hours12}:${formattedMinutes} ${period}`;
}

export function formatDelayString(delayInMinutes: number | null, timeUnitId: number | null): string | null {
  if (!delayInMinutes || !timeUnitId) {
    return null;
  }

  const converted = convertMinutesToUnit(delayInMinutes, timeUnitId);
  let unitName = TimeUnitEnum[timeUnitId]?.toLowerCase();

  if (converted === 1 && unitName?.endsWith('s')) {
    unitName = unitName.slice(0, -1);
  } else if (converted !== 1 && !unitName?.endsWith('s')) {
    unitName = `${unitName}s`;
  }
  return converted ? `${converted} ${unitName}` : null;
}

export function validateWithSchema(schema: any, data: any, skipValidation = false): ValidationWrapper {
  if (skipValidation) {
    return new ValidationWrapper([]);
  }

  try {
    schema.validateSync(data, { abortEarly: false });
    return new ValidationWrapper([]);
  } catch (e) {
    if (e instanceof Error && 'inner' in e) {
      return new ValidationWrapper(
        (e as any).inner.map((error: any) => ({
          message: error.message,
          path: error.path,
        }))
      );
    }
    return new ValidationWrapper([]);
  }
}

export class ValidationWrapper {
  constructor(private readonly errors: ValidationError[]) {}

  getError(path: string): ValidationError | undefined {
    return this.errors.find((error) => error.path === path);
  }
}
